import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CardCarousel from '@legacy-vfuk/core-card-carousel';
import SectionTitledArticlesProps from './types/SectionTitledArticles.types';
import RichTextElement from '../../Elements/RichText';
import Header from '../../Elements/Header';
import CtaButtonRow from '../../Elements/CtaButtons/CtaButtonRow';
import { ArticleCard } from '../../Elements/ArticleCard';
import VideoSummary from '../../../Articles/VideoSummary';
import Card from '../../../Articles/Card';
import {
  CarouselItemWrapper,
  CtaButtonWrapper,
  ArticlesWrapper,
  CardsWrapper,
  CarouselWrapper,
  IconsAndTextCardsWrapper,
  HeaderWrapper,
  SeeAllMediaButton,
  ContentWrapper,
} from './SectionTitledArticles.styles';
import CardWithIconAndText from '../../../Articles/CardWithIconsAndText';
import { compareByFeatured } from '../../../../helpers/articleHelpers';
import { IArticle } from '../SectionFilterableArticles/types/SectionFilterableArticles.types';
import { DirectionContext } from '../../../../contexts/DirectionContext';
import Icon from '@legacy-vfuk/core-icon';
import fireButtonAndLinkClicksEvent from '../../../../helpers/fireButtonAndLinkClicksEvent';

const MOBILE_VIEW_WIDTH = 794;
const CONTENTFUL_SECTION_ELEMENT_RICH_TEXT = 'ContentfulSectionElementRichText';
const CONTENTFUL_SECTION_ELEMENT_HEADER = 'ContentfulSectionElementHeader';
const CONTENTFUL_SECTION_ELEMENT_ARTICLE_INDEX =
  'ContentfulSectionElementArticleIndex';
const CONTENTFUL_SECTION_ELEMENT_VIDEO_ARTICLE =
  'ContentfulSectionElementVideoArticle';
const CONTENTFUL_PAGE_ARTICLE = 'ContentfulPageArticle';
const CONTENTFUL_SECTION_ELEMENT_CARD = 'ContentfulSectionElementCard';
const CONTENTFUL_SECTION_ELEMENT_BUTTON_ROW =
  'ContentfulSectionElementCtaButtonRow';


const fireCarouselArrowClickEvent = (
  carouselDirection: 'nextArrow' | 'pager' | 'previousArrow'
) => {
  try {
    if(window.onCarouselArrowClickEvent) {
      if (carouselDirection === 'nextArrow') {
        window.onCarouselArrowClickEvent('right');
      } else if (carouselDirection === 'previousArrow') {
        window.onCarouselArrowClickEvent('left');
      }
    }
    // fire the utag link event
  } catch (e) {
    console.log(
      'onCarouselArrowClickEvent failed - please check the Carousel Arrow Interactions SEO script'
    );
  }
};

const SectionTitledArticles = ({
  content,
  backgroundColor,
  slug,
  seoTitle,
  withCarousel = false,
  readingTimeMinuteLabel,
  displaySaveIcon,
  saveLabelText,
  saveIconClicked,
  onMediaArticle,
  seeAllLabel,
  seeAllUrl,
}: SectionTitledArticlesProps) => {
  const summaryColor = backgroundColor === 'white' ? 'gallery' : 'white';
  const direction = useContext(DirectionContext);

  const handleSeeAllClick = () => {
    fireButtonAndLinkClicksEvent({
      event_label: 'See All Media',
      event_action: 'link click',
    });
  };
  // TODO: Check if this is still needed after updating this file to use WS10 Source components
  useEffect(() => {
    setTimeout(() => {
      const articleContainers = document.querySelectorAll(
        '.page-article-container'
      );
      if (articleContainers.length > 0) {
        const heights = [].slice
          .call(articleContainers)
          .map(article => article.clientHeight);
        const maxHeight = Math.max(...heights);
        for (const container of articleContainers) {
          container.style.height = maxHeight + 'px';
        }
      }
    }, 500);
  }, []);

  useEffect(() => {
    const foundNextButton = document.querySelector('.carousel__next-button');
    if (foundNextButton) {
      foundNextButton.onclick = () => {
        fireCarouselArrowClickEvent('nextArrow');
      };
    }
    const foundBackButton = document.querySelector('.carousel__back-button');
    if (foundBackButton) {
      foundBackButton.onclick = () => {
        fireCarouselArrowClickEvent('previousArrow');
      };
    }
  }, []);

  const theContent = content.map(item => {
    switch (item?.__typename) {
      case CONTENTFUL_SECTION_ELEMENT_HEADER: {
        // render the article index header with a Heading component/
        if (onMediaArticle && seeAllLabel && seeAllUrl) {
          return (
            <HeaderWrapper>
              <Header key={item.id} content={item} />
              {onMediaArticle && seeAllLabel && seeAllUrl && (
                <SeeAllMediaButton href={seeAllUrl} onClick={handleSeeAllClick}>
                  {seeAllLabel}
                  <Icon
                    name={
                      direction === 'rtl' ? 'chevron-left' : 'chevron-right'
                    }
                    appearance="secondary"
                    size={2}
                  />
                </SeeAllMediaButton>
              )}
            </HeaderWrapper>
          );
        } else {
          return <Header key={item.id} content={item} />;
        }
      }

      case CONTENTFUL_SECTION_ELEMENT_RICH_TEXT: {
        // render the article index header with rich text.
        return (
          <RichTextElement
            key={item.id}
            text={item.text}
            color={item.color}
            textAlign={item.textAlign}
          />
        );
      }

      case CONTENTFUL_SECTION_ELEMENT_ARTICLE_INDEX: {
        // render the article index articles
        const { id, articles, carouselContainer, desktopNumberOfColumns } =
          item;

        const hasCarousel = carouselContainer || withCarousel;

        if (item.articles) {
          return makeArticlesAndVideos(
            id,
            slug,
            articles,
            hasCarousel,
            desktopNumberOfColumns,
            summaryColor,
            seoTitle,
            readingTimeMinuteLabel,
            displaySaveIcon,
            saveLabelText,
            saveIconClicked,
            onMediaArticle
          );
        }
        // There is an empty articles array
        return false;
      }

      case CONTENTFUL_SECTION_ELEMENT_BUTTON_ROW:
        return (
          <CtaButtonWrapper key={item.id}>
            <CtaButtonRow
              key={item.id}
              buttonRowAlignment={item.buttonRowAlignment}
              buttons={item.buttons}
            />
          </CtaButtonWrapper>
        );

      default:
        return false;
    }
  });

  return <ContentWrapper>{theContent}</ContentWrapper>;
};

SectionTitledArticles.propTypes = {
  content: PropTypes.array,
  backgroundColor: PropTypes.oneOf(['white', 'gallery']),
};

let isCardContent = false;
let isIconsAndText = false;

const makeArticlesAndVideos = (
  id: string,
  slug: string,
  articles: Array<IArticle>,
  carouselContainer: any,
  desktopNumberOfColumns: number,
  backgroundColor: 'white' | 'gallery',
  seoTitle: string,
  readingTimeMinuteLabel: string,
  displaySaveIcon: boolean,
  saveLabelText: string,
  handleSaveClick: (articleSlug?: string) => void,
  onMediaArticle?: boolean
) => {
  const isMobile = checkWindowSize();

  function checkWindowSize() {
    const [isMobile, setIsMobile] = useState(false);
    if (typeof window !== 'undefined') {
      useEffect(() => {
        function handleResize() {
          if (window.innerWidth <= MOBILE_VIEW_WIDTH) {
            setIsMobile(true);
          }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    }
    return isMobile;
  }

  let isSingleVideoFeature = false;
  const DEFAULT_NUMBER_OF_COLUMNS = 2;
  const numberOfColumns = desktopNumberOfColumns
    ? desktopNumberOfColumns
    : DEFAULT_NUMBER_OF_COLUMNS;
  const nosOfArticles = articles.length;
  const hasCarousel = carouselContainer;
  const mobileLimit = 3;

  const theArticles = articles.sort(compareByFeatured).map((article, index) => {
    switch (article.__typename) {
      case CONTENTFUL_SECTION_ELEMENT_VIDEO_ARTICLE: {
        isSingleVideoFeature = articles.length === 1;
        if (hasCarousel && nosOfArticles >= 2) {
          return (
            <CarouselItemWrapper>
              <VideoSummary key={article.id} data={article} />
            </CarouselItemWrapper>
          );
        } else if (nosOfArticles === 3) {
          return (
            <CarouselItemWrapper>
              <VideoSummary key={article.id} data={article} />
            </CarouselItemWrapper>
          );
        } else {
          return (
            <VideoSummary
              key={article.id}
              data={article}
              backgroundColor={backgroundColor}
            />
          );
        }
      }

      case CONTENTFUL_PAGE_ARTICLE: {
        // The carousel container will display if the carouselContainer in Contentful is selected
        // & the number of articles are 4 or more
        isCardContent = false;
        if (hasCarousel && nosOfArticles >= 2) {
          return (
            <CarouselItemWrapper className="page-article-container">
              <ArticleCard
                isFeatured={article.displayAsFeatured}
                key={article.id}
                indexSlug={slug}
                data={article}
                backgroundColor={backgroundColor}
                seoTitle={seoTitle}
                readingTimeMinuteLabel={readingTimeMinuteLabel}
                displaySaveIcon={displaySaveIcon}
                saveLabelText={saveLabelText}
                handleSaveClick={handleSaveClick}
                isAnimated={true}
              />
            </CarouselItemWrapper>
          );
        } else {
          if (!isMobile || (isMobile && index < mobileLimit)) {
            return (
              <ArticleCard
                isFeatured={article.displayAsFeatured}
                key={article.id}
                indexSlug={slug}
                data={article}
                backgroundColor={backgroundColor}
                seoTitle={seoTitle}
                readingTimeMinuteLabel={readingTimeMinuteLabel}
                displaySaveIcon={displaySaveIcon}
                saveLabelText={saveLabelText}
                handleSaveClick={handleSaveClick}
                isAnimated={true}
              />
            );
          } else {
            return null;
          }
        }
      }

      case CONTENTFUL_SECTION_ELEMENT_CARD:
        isCardContent = true;
        if (article.iconsAndText.length > 0) {
          isIconsAndText = true;
          return <CardWithIconAndText key={article.id} data={article} />;
        } else {
          isIconsAndText = false;
          return (
            <Card
              key={article.id}
              data={article}
              backgroundColor={backgroundColor}
            />
          );
        }

      default:
        return false;
    }
  });

  if (hasCarousel && nosOfArticles >= 2) {
    return (
      <CarouselWrapper>
        <CardCarousel
          srName="carousel-article"
          slidesToShow={{ xl: isMobile ? 1 : onMediaArticle ? 3 : 2 }}
          slidesToScroll={{ xl: 1, lg: 1, md: 1, sm: 1 }}
          id={id}
          arrows={{
            position: 'overlap',
            onClick: payload => {
              fireCarouselArrowClickEvent(payload.trigger);
            },
          }}
        >
          {theArticles}
        </CardCarousel>
      </CarouselWrapper>
    );
  }

  if (isSingleVideoFeature) {
    return <>{theArticles}</>;
  }

  if (isCardContent) {
    return isIconsAndText ? (
      <IconsAndTextCardsWrapper numberOfColumns={numberOfColumns} key={id}>
        {theArticles}
      </IconsAndTextCardsWrapper>
    ) : (
      <CardsWrapper numberOfColumns={numberOfColumns} key={id}>
        {theArticles}
      </CardsWrapper>
    );
  } else {
    return (
      <ArticlesWrapper numberOfColumns={numberOfColumns} key={id}>
        {theArticles}
      </ArticlesWrapper>
    );
  }
};

export default SectionTitledArticles;
